import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DatePickerPanel from './DatePickerPanel';
import { DATE, DATETIME, formatValue, isInRange, makeValue, normalizeValue, parseTyped, todayKey, typedProblem } from '../../utils/datePickerUtils';
import { backspace, clearAll, editText, editToTyped, eraseActive, isEmptyEdit, moveTo, sectionAt, sectionRange, startEdit, typeKey } from '../../utils/datePickerSegments';
import '../css/DatePicker.css';

const GAP = 6;
const EDGE = 8;

/** Fixed position under the input, else above it, else wherever it stays on screen. Never clipped by a scrolling modal. */
const placePanel = (input, panel) => {
  const rect = input.getBoundingClientRect();
  const left = Math.max(EDGE, Math.min(rect.left, window.innerWidth - panel.offsetWidth - EDGE));
  if (rect.bottom + GAP + panel.offsetHeight <= window.innerHeight - EDGE) return { left, top: rect.bottom + GAP };
  if (rect.top - GAP - panel.offsetHeight >= EDGE) return { left, bottom: window.innerHeight - rect.top + GAP };
  return { left, top: Math.max(EDGE, window.innerHeight - panel.offsetHeight - EDGE) };
};

/**
 * The app's own date input (IGFEMS-673): type section by section, or pick from the calendar. Works
 * like a native date input: value, min and max are strings and onChange receives { target: { name, value } }.
 * Default variant "date" uses "YYYY-MM-DD"; variant "datetime" uses "YYYY-MM-DDTHH:mm" (24-hour).
 * Typed text saves on Enter or leaving the field. On Enter, text that is not a real or allowed date
 * stays in place with a message. On leaving the field, it clears.
 */
const DatePicker = ({
  id,
  name,
  value,
  min,
  max,
  onChange,
  variant = DATE,
  className = 'form-control',
  placeholder = variant === DATETIME ? 'dd-MMM-yyyy HH:mm' : 'dd-MMM-yyyy',
  disabled,
}) => {
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [enterRefused, setEnterRefused] = useState(false);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  // While the mouse is down the browser owns the caret, and handleMouseDown reads it once it lands.
  const pointerDownRef = useRef(false);

  const current = normalizeValue(value, variant);
  const changed = edit?.dirty ? edit : null;
  const typed = changed && !isEmptyEdit(changed) ? editToTyped(changed) : '';
  const parsed = typed ? parseTyped(typed, variant) : '';
  const selected = parsed && isInRange(parsed, min, max) ? parsed : current;
  const problem = enterRefused && typed ? typedProblem(typed, variant, min, max) : '';

  const emit = (next) => {
    setEdit(null);
    setEnterRefused(false);
    if (next !== current) onChange({ target: { name, value: next } });
  };

  const commitEdit = () => {
    if (!edit) return;
    if (!changed) setEdit(null);
    else emit(parsed && isInRange(parsed, min, max) ? parsed : '');
  };

  const applyEdit = (next) => {
    setEdit(next);
    setEnterRefused(false);
    setOpen(true);
  };

  const handleKeyDown = (e) => {
    pointerDownRef.current = false;
    if (e.key === 'Enter') {
      e.preventDefault();
      if (problem || (typed && typedProblem(typed, variant, min, max))) {
        setEnterRefused(true);
        setOpen(true);
        return;
      }
      commitEdit();
      setOpen(false);
      return;
    }
    if (e.key === 'Escape') {
      if (!open) return;
      // Closes the calendar, not a modal around it.
      e.stopPropagation();
      commitEdit();
      setOpen(false);
      return;
    }
    if (e.key === 'Tab') {
      setOpen(false);
      return;
    }
    // Leaves Ctrl+A, Ctrl+C and the like to the browser.
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    const base = edit ?? startEdit(current, variant);
    const input = e.target;
    const allSelected = input.value && input.selectionStart === 0 && input.selectionEnd === input.value.length;

    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      if (allSelected) applyEdit(clearAll(base));
      else applyEdit(e.key === 'Backspace' ? backspace(base) : eraseActive(base));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      applyEdit(moveTo(base, base.active - 1));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      applyEdit(moveTo(base, base.active + 1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      applyEdit(moveTo(base, 0));
    } else if (e.key === 'End') {
      e.preventDefault();
      applyEdit(moveTo(base, base.parts.length - 1));
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    } else if (e.key.length === 1) {
      e.preventDefault();
      const next = typeKey(allSelected ? clearAll(base) : base, e.key);
      // The same state means the key was refused, so the section stays selected as it is.
      if (next !== base) applyEdit(next);
    }
  };

  const handleMouseDown = (e) => {
    pointerDownRef.current = true;
    // A click inside a selection would only collapse it after mouseup, undoing the selection below.
    e.target.setSelectionRange(e.target.selectionStart, e.target.selectionStart);
    // On the document, not onClick: a click never fires if the mouse is released outside the input.
    document.addEventListener('mouseup', () => {
      pointerDownRef.current = false;
      const input = inputRef.current;
      if (input !== document.activeElement) return;
      const caret = input.selectionStart;
      setEdit((prev) => {
        const base = prev ?? startEdit(current, variant);
        return moveTo(base, sectionAt(base, caret));
      });
      setOpen(true);
    }, { once: true });
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = parseTyped(e.clipboardData.getData('text'), variant);
    if (pasted) applyEdit({ ...startEdit(pasted, variant), dirty: true });
  };

  const handlePickDay = (dayKey) => {
    emit(makeValue(dayKey, selected.slice(11, 16), variant));
    if (variant !== DATETIME) setOpen(false);
  };

  // Keeps the active section highlighted, as a native date input does.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (edit && input === document.activeElement && !pointerDownRef.current) {
      input.setSelectionRange(...sectionRange(edit, edit.active));
    }
  }, [edit]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => setPosition(placePanel(inputRef.current, panelRef.current));
    const closeOnOutsideClick = (e) => {
      if (!inputRef.current.contains(e.target) && !panelRef.current.contains(e.target)) setOpen(false);
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', closeOnOutsideClick);
    };
    // The message makes the panel taller, so it may need to move.
  }, [open, problem]);

  return (
    <>
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        className={problem ? `${className} ems-dp-input--invalid` : className}
        aria-invalid={Boolean(problem)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        aria-haspopup="dialog"
        aria-expanded={open}
        value={edit ? editText(edit) : formatValue(current)}
        // Every edit goes through handleKeyDown or handlePaste, so anything else is put back.
        onChange={() => {}}
        onFocus={() => { setEdit(startEdit(current, variant)); setOpen(true); }}
        onMouseDown={handleMouseDown}
        onBlur={commitEdit}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      />
      {open && createPortal(
        <DatePickerPanel
          // A new selected month shows that month, including one reached by typing.
          key={selected.slice(0, 7)}
          panelRef={panelRef}
          style={position ?? { visibility: 'hidden' }}
          variant={variant}
          selected={selected}
          min={min}
          max={max}
          problem={problem}
          onPickDay={handlePickDay}
          onPickTime={(time) => emit(makeValue(selected.slice(0, 10), time, variant))}
          onToday={() => handlePickDay(todayKey())}
          onClear={() => { emit(''); setOpen(false); }}
          onDone={() => { commitEdit(); setOpen(false); }}
        />,
        document.body
      )}
    </>
  );
};

export default DatePicker;
